import { useMemo, useState } from 'react';
import type { FlashcardRatingPeriod } from '../../services/flashcardService';
import type { Rating } from '../../lib/spacedRepetition/sm2';

const RATING_META: Record<Rating, { label: string; color: string; bg: string }> = {
  again: { label: 'Again', color: '#ef4444', bg: 'bg-red-500' },
  hard: { label: 'Hard', color: '#f97316', bg: 'bg-orange-500' },
  good: { label: 'Good', color: '#22c55e', bg: 'bg-green-500' },
  easy: { label: 'Easy', color: '#3b82f6', bg: 'bg-blue-500' },
};

const PERIOD_OPTIONS: { id: FlashcardRatingPeriod; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: '7 days' },
  { id: 'month', label: 'Month' },
];

interface FlashcardRatingsPanelProps {
  ratingCountsByPeriod: Record<FlashcardRatingPeriod, Record<Rating, number>>;
}

const FlashcardRatingsPanel = ({ ratingCountsByPeriod }: FlashcardRatingsPanelProps) => {
  const [period, setPeriod] = useState<FlashcardRatingPeriod>('week');

  const counts = ratingCountsByPeriod[period];
  const total = useMemo(
    () => counts.again + counts.hard + counts.good + counts.easy,
    [counts]
  );

  const rows = useMemo(() => {
    const max = Math.max(1, ...(['again', 'hard', 'good', 'easy'] as Rating[]).map((k) => counts[k]));
    return (['again', 'hard', 'good', 'easy'] as Rating[]).map((key) => ({
      key,
      ...RATING_META[key],
      count: counts[key],
      pct: total > 0 ? Math.round((counts[key] / total) * 100) : 0,
      barPct: (counts[key] / max) * 100,
    }));
  }, [counts, total]);

  const emptyMessages: Record<FlashcardRatingPeriod, string> = {
    today: 'No reviews yet today — start a session!',
    week: 'No reviews in the last 7 days.',
    month: 'No reviews in the last 30 days.',
  };

  return (
    <div className="bg-white/80 backdrop-blur rounded-2xl p-4 sm:p-6 shadow-lg border border-white/60">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <h3 className="text-sm font-black text-gray-700 uppercase tracking-wide">Your ratings</h3>
        <div className="inline-flex p-1 rounded-xl bg-gray-100/90 self-start sm:self-auto">
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setPeriod(opt.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                period === opt.id
                  ? 'bg-white text-indigo-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {total === 0 ? (
        <p className="text-sm text-gray-500 py-6 text-center">{emptyMessages[period]}</p>
      ) : (
        <>
          <p className="text-xs font-semibold text-gray-500 mb-4 text-center sm:text-left">
            <span className="text-2xl font-black text-gray-900">{total}</span> review{total !== 1 ? 's' : ''} in
            this period
          </p>
          <div className="space-y-3">
            {rows.map((row) => (
              <div key={row.key} className="flex items-center gap-3">
                <span className="w-12 text-xs font-bold text-gray-600 shrink-0">{row.label}</span>
                <div className="flex-1 h-8 rounded-lg bg-gray-100 overflow-hidden relative">
                  <div
                    className={`h-full rounded-lg ${row.bg} transition-all duration-500 ease-out`}
                    style={{ width: `${row.barPct}%`, minWidth: row.count > 0 ? '8px' : 0 }}
                  />
                </div>
                <span className="w-14 text-right text-xs font-bold text-gray-700 shrink-0">
                  {row.count}
                  <span className="text-gray-400 font-semibold ml-0.5">({row.pct}%)</span>
                </span>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-3 mt-5 pt-4 border-t border-gray-100">
            {rows
              .filter((r) => r.count > 0)
              .map((r) => (
                <span
                  key={r.key}
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-600"
                >
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: r.color }} />
                  {r.label}
                </span>
              ))}
          </div>
        </>
      )}
    </div>
  );
};

export default FlashcardRatingsPanel;
